// Preguntas abiertas.
//
// Convierte las respuestas de texto libre en análisis general, temas, tendencias
// y categorías — opcionalmente comparadas entre segmentos definidos por preguntas
// cerradas (rangos de edad, sexo, distrito). Cada análisis queda guardado y
// asociado a su pregunta para consultarlo después.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Plot from "../components/Plot";
import ProjectFormPicker from "../components/ProjectFormPicker";
import {
  Card,
  ErrorBanner,
  HelpBox,
  InfoBanner,
  OkBanner,
  PageHeader,
  Spinner,
  AccionesSeleccion,
} from "../components/Layout";
import {
  OdkAPI,
  OpenTextAPI,
  type OpenAnalysis,
  type OpenEstimate,
  type OpenOptions,
  type OpenResultado,
} from "../api/endpoints";

const ESTRATEGIAS = [
  {
    key: "todas",
    label: "Todas las respuestas",
    desc: "Manda todas las respuestas, sin tope. Si no caben en una llamada se trocean y se consolidan.",
  },
  {
    key: "muestra",
    label: "Muestra",
    desc: "Analiza una muestra aleatoria reproducible. Con cruce, el sorteo es por segmento para que ninguno se quede fuera.",
  },
  {
    key: "lotes",
    label: "Por lotes",
    desc: "Resume por bloques y luego consolida. Lo más barato cuando hay mucho material.",
  },
];

function descargar(blob: Blob, nombre: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(url);
}

function Resultado({ r }: { r: OpenResultado }) {
  const categorias = r.categorias ?? [];
  const temas = r.temas ?? [];
  const porSegmento = Object.entries(r.por_segmento ?? {});

  return (
    <div className="space-y-5">
      {(r.advertencias ?? []).length > 0 && (
        <div className="space-y-2">
          {r.advertencias!.map((a, i) => (
            <InfoBanner key={i} message={a} />
          ))}
        </div>
      )}

      {r.resumen_general && (
        <div>
          <div className="text-sm font-medium text-tinta-2 mb-1">Análisis general</div>
          <p className="text-sm text-tinta leading-relaxed whitespace-pre-line">{r.resumen_general}</p>
        </div>
      )}

      {temas.length > 0 && (
        <div>
          <div className="text-sm font-medium text-tinta-2 mb-2">Temas principales</div>
          <div className="space-y-2">
            {temas.map((t, i) => (
              <div key={i} className="border border-grid rounded-md p-3 bg-plano">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-tinta">{t.tema}</span>
                  <span className="text-xs text-tinta-3">
                    peso {(t.peso ?? 0).toFixed(2)}
                  </span>
                </div>
                {(t.ejemplos ?? []).length > 0 && (
                  <ul className="mt-1 text-xs text-tinta-2 list-disc list-inside space-y-0.5">
                    {t.ejemplos!.map((e, j) => (
                      <li key={j}>«{e}»</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(r.tendencias ?? []).length > 0 && (
        <div>
          <div className="text-sm font-medium text-tinta-2 mb-1">Tendencias</div>
          <ul className="text-sm text-tinta-2 list-disc list-inside space-y-1">
            {r.tendencias!.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>
      )}

      {categorias.length > 0 && (
        <div>
          <div className="text-sm font-medium text-tinta-2 mb-2">Categorías</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-tinta-3 border-b border-grid">
                <tr>
                  <th className="text-left py-2 pr-3">Categoría</th>
                  <th className="text-left py-2 pr-3">Definición</th>
                  <th className="text-right py-2 pr-3">%</th>
                </tr>
              </thead>
              <tbody>
                {categorias.map((c, i) => (
                  <tr key={i} className="border-b border-grid">
                    <td className="py-2 pr-3 font-medium text-tinta">{c.nombre}</td>
                    <td className="py-2 pr-3 text-tinta-2">{c.definicion}</td>
                    <td className="py-2 pr-3 text-right text-marca">
                      {c.pct != null ? `${c.pct.toFixed(1)}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {categorias.some((c) => c.pct != null) && (
            <div className="h-64 mt-3">
              <Plot
                data={[{
                  type: "bar",
                  orientation: "h",
                  x: categorias.map((c) => c.pct ?? 0),
                  y: categorias.map((c) => c.nombre),
                }]}
                layout={{ margin: { t: 10, r: 20, b: 40 }, xaxis: { title: { text: "%" } } }}
              />
            </div>
          )}
        </div>
      )}

      {porSegmento.length > 0 && (
        <div>
          <div className="text-sm font-medium text-tinta-2 mb-2">Por segmento</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-tinta-3 border-b border-grid">
                <tr>
                  <th className="text-left py-2 pr-3">Segmento</th>
                  <th className="text-left py-2 pr-3">Qué dice</th>
                  <th className="text-left py-2 pr-3">En qué se diferencia</th>
                </tr>
              </thead>
              <tbody>
                {porSegmento.map(([seg, datos]) => (
                  <tr key={seg} className="border-b border-grid align-top">
                    <td className="py-2 pr-3 font-medium text-marca whitespace-nowrap">{seg}</td>
                    <td className="py-2 pr-3 text-tinta-2">{datos.resumen}</td>
                    <td className="py-2 pr-3 text-tinta-2">{datos.diferencias}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {r.comparacion_entre_segmentos && (
        <div>
          <div className="text-sm font-medium text-tinta-2 mb-1">Comparación entre segmentos</div>
          <p className="text-sm text-tinta leading-relaxed whitespace-pre-line">
            {r.comparacion_entre_segmentos}
          </p>
        </div>
      )}

      {(r.citas_representativas ?? []).length > 0 && (
        <div>
          <div className="text-sm font-medium text-tinta-2 mb-1">Citas representativas</div>
          <ul className="text-sm text-tinta-2 space-y-1">
            {r.citas_representativas!.map((c, i) => (
              <li key={i} className="border-l-2 border-marca/40 pl-3 italic">«{c}»</li>
            ))}
          </ul>
        </div>
      )}

      {r._meta && (
        <p className="text-xs text-tinta-3 border-t border-grid pt-3">
          Analizadas {r._meta.n_analizadas} de {r._meta.n_disponibles} respuestas
          {" "}(estrategia «{r._meta.estrategia}»
          {r._meta.n_lotes > 1 ? `, en ${r._meta.n_lotes} bloques` : ""}).
          {" "}Descartadas: {r._meta.descartes.vacias} vacías, {r._meta.descartes.descartadas} sin
          contenido útil, {r._meta.descartes.recortadas} recortadas por longitud.
          {r._meta.fecha_desde && ` Recogidas entre ${r._meta.fecha_desde} y ${r._meta.fecha_hasta}.`}
        </p>
      )}
    </div>
  );
}

function Guardado({ a, onBorrar }: { a: OpenAnalysis; onBorrar: (id: number) => void }) {
  const [abierto, setAbierto] = useState(false);
  const [verPrompt, setVerPrompt] = useState(false);
  const detalle = useQuery({
    queryKey: ["open-text", a.id],
    queryFn: () => OpenTextAPI.get(a.id),
    enabled: abierto,
  });

  const pdf = useMutation({
    mutationFn: () => OpenTextAPI.pdf({ analysis_id: a.id }),
    onSuccess: (blob) => descargar(blob, `abiertas_${a.id}.pdf`),
  });

  return (
    <div className="border border-grid rounded-md mb-2 bg-superficie">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <button className="text-sm text-marca font-medium" onClick={() => setAbierto(!abierto)}>
          {abierto ? "▾" : "▸"} {a.question_label || a.question_name}
        </button>
        <span className="text-xs text-tinta-3">
          {a.created_at?.replace("T", " ").slice(0, 16)} · {a.n_analizadas}/{a.n_total} respuestas
          {a.cross_vars.length > 0 && ` · cruzada por ${a.cross_vars.join(", ")}`}
          {a.costo > 0 && ` · $${a.costo.toFixed(4)}`}
        </span>
        {a.status !== "ok" && (
          <span className="text-[11px] px-2 py-0.5 rounded bg-estado-critico/10 text-estado-critico border border-estado-critico/30">
            falló
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {a.status === "ok" && (
            <button className="btn-sec text-xs" disabled={pdf.isPending} onClick={() => pdf.mutate()}>
              {pdf.isPending ? "Generando…" : "PDF"}
            </button>
          )}
          <button className="btn-peligro text-xs" onClick={() => onBorrar(a.id)}>Borrar</button>
        </div>
      </div>

      {abierto && (
        <div className="px-3 pb-3 border-t border-grid pt-3">
          {a.status !== "ok" && a.error && <ErrorBanner message={a.error} />}
          {detalle.isLoading && <div className="flex items-center gap-2 text-tinta-3"><Spinner /> Cargando…</div>}
          {detalle.data?.resultado && <Resultado r={detalle.data.resultado} />}
          {detalle.data && (
            <div className="mt-4">
              <button className="text-xs text-tinta-3 underline" onClick={() => setVerPrompt(!verPrompt)}>
                {verPrompt ? "Ocultar" : "Ver"} el prompt exacto que se usó
              </button>
              {verPrompt && (
                <pre className="mt-2 text-[11px] font-mono bg-plano border border-grid rounded p-3 overflow-x-auto whitespace-pre-wrap">
{detalle.data.prompt_sistema}
{"\n\n──────── datos ────────\n\n"}
{detalle.data.prompt_usuario}
                </pre>
              )}
            </div>
          )}
          {pdf.isError && <div className="mt-2"><ErrorBanner message={(pdf.error as Error).message} /></div>}

          {detalle.data?.status === "ok" && <Complementar a={detalle.data} />}
        </div>
      )}
    </div>
  );
}


// Ampliar un análisis ya hecho, sin volver a hacerlo.
//
// Al modelo se le pasa el resultado guardado y lo que falta, NO las respuestas
// crudas: por eso cuesta una llamada en vez del análisis entero. La contrapartida
// se dice aquí, no se esconde: sin las respuestas delante no puede contar ni citar
// textualmente, y si se le pide lo dirá en «lo que no se puede responder».
function Complementar({ a }: { a: OpenAnalysis }) {
  const qc = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [instruccion, setInstruccion] = useState("");

  const enviar = useMutation({
    mutationFn: () => OpenTextAPI.complementar(a.id, instruccion),
    onSuccess: () => {
      setInstruccion("");
      qc.invalidateQueries({ queryKey: ["open-text", a.id] });
      qc.invalidateQueries({ queryKey: ["open-text-historico", a.project_id, a.form_id] });
    },
  });

  const hijos = a.complementos ?? [];

  return (
    <div className="mt-4 border-t border-grid pt-3">
      <div className="flex flex-wrap items-center gap-2">
        <button className="text-sm text-marca font-medium" onClick={() => setAbierto(!abierto)}>
          {abierto ? "▾" : "▸"} Complementar este análisis
        </button>
        {hijos.length > 0 && (
          <span className="text-xs text-tinta-3">
            {hijos.length} complemento{hijos.length === 1 ? "" : "s"} ya hecho
            {hijos.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {hijos.length > 0 && (
        <ul className="mt-2 text-xs text-tinta-2 list-disc pl-5 space-y-0.5">
          {hijos.map((c) => (
            <li key={c.id}>
              «{c.instruccion}»{" "}
              <span className="text-tinta-3">
                ({c.created_at?.replace("T", " ").slice(0, 16)}
                {c.status !== "ok" && " · falló"})
              </span>{" "}
              <span className="text-tinta-3">— está más abajo, en la lista de guardados</span>
            </li>
          ))}
        </ul>
      )}

      {abierto && (
        <div className="mt-3">
          <p className="text-xs text-tinta-3 mb-2">
            Escribe qué quieres <strong>además</strong> de lo que ya dice este análisis. Se le
            pasa el resultado de arriba, no las {a.n_analizadas} respuestas otra vez: por eso
            cuesta una sola llamada.{" "}
            <strong>
              Por lo mismo no puede contar cuántas personas dijeron algo exacto ni citar
              textualmente
            </strong>
            ; si se lo pides, lo dirá en vez de inventárselo. El original no se toca: esto se
            guarda aparte, colgado de él.
          </p>
          <textarea
            className="w-full border border-grid rounded-md px-3 py-2 text-sm bg-superficie"
            rows={3}
            placeholder="Ej.: profundiza en lo que dicen sobre los tiempos de espera, y contrasta con lo que decían los mayores de 50"
            value={instruccion}
            onChange={(e) => setInstruccion(e.target.value)}
          />
          <div className="flex items-center gap-3 mt-2">
            <button
              className="btn"
              disabled={enviar.isPending || !instruccion.trim()}
              onClick={() => enviar.mutate()}
            >
              {enviar.isPending ? "Preguntando…" : "Complementar"}
            </button>
            {enviar.isPending && <Spinner />}
            {!instruccion.trim() && (
              <span className="text-xs text-tinta-3">
                Sin instrucción, complementar no significa nada.
              </span>
            )}
          </div>
          {enviar.isError && (
            <div className="mt-2"><ErrorBanner message={(enviar.error as Error).message} /></div>
          )}
          {enviar.isSuccess && (
            <div className="mt-3">
              <OkBanner message="Complemento guardado. Queda colgado de este análisis y aparece también en la lista de guardados." />
              {enviar.data.resultado && <Resultado r={enviar.data.resultado} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function OpenTextPage() {
  const qc = useQueryClient();
  const [projectId, setProjectId] = useState<number | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [preguntas, setPreguntas] = useState<string[]>([]);
  const [cruces, setCruces] = useState<string[]>([]);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [opciones, setOpciones] = useState<OpenOptions>({});
  const [transversal, setTransversal] = useState(false);
  const [estimacion, setEstimacion] = useState<OpenEstimate | null>(null);

  const listo = !!projectId && !!formId && preguntas.length > 0;

  const abiertasQ = useQuery({
    queryKey: ["open-questions", projectId, formId],
    queryFn: () => OpenTextAPI.questions(projectId!, formId!),
    enabled: !!projectId && !!formId,
  });

  const variablesQ = useQuery({
    queryKey: ["variables", projectId, formId],
    queryFn: () => OdkAPI.variables(projectId!, formId!),
    enabled: !!projectId && !!formId,
  });

  const cerradas = useMemo(
    () => (variablesQ.data ?? []).filter((v) => (v.type ?? "").startsWith("select_")),
    [variablesQ.data]
  );

  const historicoQ = useQuery({
    queryKey: ["open-text-historico", projectId, formId],
    queryFn: () => OpenTextAPI.history(projectId!, formId!),
    enabled: !!projectId && !!formId,
  });

  const cuerpo = () => ({
    project_id: projectId!,
    form_id: formId!,
    questions: preguntas,
    cross_vars: cruces,
    fecha_desde: desde || null,
    fecha_hasta: hasta || null,
    opciones,
    lectura_transversal: transversal,
  });

  const estimar = useMutation({
    mutationFn: () => OpenTextAPI.estimate(cuerpo()),
    onSuccess: setEstimacion,
    onError: () => setEstimacion(null),
  });

  const analizar = useMutation({
    mutationFn: () => OpenTextAPI.run(cuerpo()),
    // `onSettled` y no `onSuccess`: si la conexión se corta, el servidor pudo
    // terminar y guardar igualmente. Refrescar el histórico también al fallar es
    // lo que permite que el usuario lo vea en vez de volver a lanzarlo.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["open-text-historico", projectId, formId] });
    },
  });

  const borrar = useMutation({
    mutationFn: (id: number) => OpenTextAPI.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["open-text-historico", projectId, formId] }),
  });

  const alternar = (lista: string[], set: (v: string[]) => void, valor: string) =>
    set(lista.includes(valor) ? lista.filter((x) => x !== valor) : [...lista, valor]);

  const estrategiaActual = opciones.estrategia ?? estimacion?.estrategia ?? "";

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <PageHeader
        title="Preguntas abiertas"
        subtitle="Lee las respuestas escritas por los encuestados y las convierte en temas, tendencias y categorías."
      />

      <HelpBox title="¿Cómo funciona esta página?">
        <p>
          Elige una o varias <b>preguntas abiertas</b> (las de texto libre del formulario). El
          modelo recibe el enunciado exacto de la pregunta, el nombre de la encuesta, las fechas
          de recolección y las respuestas, y devuelve un análisis general, los temas ordenados
          por peso, las tendencias, una propuesta de categorías y citas representativas.
        </p>
        <p>
          <b>Cruzar</b> con una o varias preguntas cerradas (rango de edad, sexo, distrito) agrupa
          las respuestas por segmento y pide además la comparación entre ellos. Los segmentos con
          muy pocas respuestas se marcan y no se comparan: una diferencia sobre cuatro respuestas
          no es una diferencia.
        </p>
        <p>
          <b>Estrategia.</b> «Todas» manda todo, sin tope. «Muestra» sortea un subconjunto
          reproducible (misma semilla, misma muestra), y con cruce el sorteo es por segmento para
          que ninguno desaparezca. «Por lotes» resume en bloques y luego consolida: es lo más
          barato cuando hay miles de respuestas. Los valores de partida salen de Configuración.
        </p>
        <p>
          <b>Estima antes de analizar.</b> El botón «Estimar» no gasta ninguna llamada: dice
          cuántas respuestas entran, cuántas llamadas hacen falta y cuánto costaría aproximadamente.
        </p>
        <p>
          Todo lo que se analiza <b>queda guardado</b> abajo, junto al prompt exacto que se envió
          — los prompts se pueden editar, así que sin eso un análisis viejo sería inexplicable.
        </p>
      </HelpBox>

      <Card className="mb-4">
        <ProjectFormPicker
          projectId={projectId}
          formId={formId}
          onProject={(id) => { setProjectId(id); setFormId(null); setPreguntas([]); setCruces([]); setEstimacion(null); }}
          onForm={(id) => { setFormId(id); setPreguntas([]); setCruces([]); setEstimacion(null); }}
        />
      </Card>

      {formId && (
        <Card className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <label className="etiqueta mb-0">Preguntas abiertas del formulario</label>
            {(abiertasQ.data ?? []).length > 0 && (
              <AccionesSeleccion
                visibles={(abiertasQ.data ?? []).map((p) => p.name)}
                seleccionados={preguntas}
                onCambiar={(v) => { setPreguntas(v); setEstimacion(null); }}
                nombre="pregunta(s)"
              />
            )}
          </div>
          {abiertasQ.isLoading && (
            <div className="flex items-center gap-2 text-tinta-3 text-sm"><Spinner /> Buscando…</div>
          )}
          {abiertasQ.isError && <ErrorBanner message={(abiertasQ.error as Error).message} />}
          {abiertasQ.data?.length === 0 && (
            <InfoBanner message="Este formulario no tiene preguntas de texto libre, o no tienes permiso sobre ellas." />
          )}
          {(abiertasQ.data ?? []).length > 0 && (
            <div className="max-h-56 overflow-y-auto bg-white border border-grid rounded-md p-2">
              {abiertasQ.data!.map((p) => (
                <label
                  key={p.name}
                  className="flex items-start gap-2 py-1 px-1 rounded cursor-pointer hover:bg-plano"
                >
                  <input
                    type="checkbox"
                    className="mt-1 accent-marca"
                    checked={preguntas.includes(p.name)}
                    onChange={() => { alternar(preguntas, setPreguntas, p.name); setEstimacion(null); }}
                  />
                  <span className="text-sm flex-1">
                    <span className="block text-tinta">{p.label || p.name}</span>
                    <span className="block text-[11px] font-mono text-tinta-3">
                      {p.name} · {p.n_respondidas} de {p.n_total} respondieron
                      {p.n_respondidas === 0 && " — no hay nada que analizar"}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}

          {cerradas.length > 0 && (
            <div className="mt-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <label className="etiqueta mb-0">
                  Cruzar por (opcional) — preguntas cerradas que definen los segmentos
                </label>
                {cerradas.length > 0 && (
                  <AccionesSeleccion
                    visibles={cerradas.map((v) => v.name)}
                    seleccionados={cruces}
                    onCambiar={(v) => { setCruces(v); setEstimacion(null); }}
                    nombre="variable(s)"
                  />
                )}
              </div>
              <div className="max-h-40 overflow-y-auto bg-white border border-grid rounded-md p-2">
                {cerradas.map((v) => (
                  <label key={v.name} className="flex items-center gap-2 py-1 px-1 rounded cursor-pointer hover:bg-plano">
                    <input
                      type="checkbox"
                      className="accent-marca"
                      checked={cruces.includes(v.name)}
                      onChange={() => { alternar(cruces, setCruces, v.name); setEstimacion(null); }}
                    />
                    <span className="text-sm text-tinta-2">{v.label || v.name}</span>
                  </label>
                ))}
              </div>
              {cruces.length > 1 && (
                <p className="mt-1 text-xs text-estado-serio">
                  Con {cruces.length} variables de cruce los segmentos se multiplican y se quedan
                  con pocas respuestas cada uno. Suele leerse mejor una sola.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="etiqueta">Desde (fecha de envío)</label>
              <input type="date" className="input" value={desde}
                     onChange={(e) => { setDesde(e.target.value); setEstimacion(null); }} />
            </div>
            <div>
              <label className="etiqueta">Hasta</label>
              <input type="date" className="input" value={hasta}
                     onChange={(e) => { setHasta(e.target.value); setEstimacion(null); }} />
            </div>
          </div>

          <div className="mt-4">
            <label className="etiqueta">Estrategia</label>
            <div className="flex flex-wrap gap-2">
              {ESTRATEGIAS.map((e) => (
                <button
                  key={e.key}
                  onClick={() => { setOpciones({ ...opciones, estrategia: e.key }); setEstimacion(null); }}
                  className={`px-2.5 py-1 rounded-md text-xs ${
                    estrategiaActual === e.key
                      ? "bg-marca text-white"
                      : "bg-plano text-tinta-2 border border-grid hover:bg-grid"
                  }`}
                >
                  {e.label}
                </button>
              ))}
              <button
                onClick={() => { setOpciones({ ...opciones, estrategia: undefined }); setEstimacion(null); }}
                className={`px-2.5 py-1 rounded-md text-xs ${
                  opciones.estrategia === undefined
                    ? "bg-marca text-white"
                    : "bg-plano text-tinta-2 border border-grid hover:bg-grid"
                }`}
                title="Usa lo configurado en Configuración → Análisis de preguntas abiertas"
              >
                La del panel
              </button>
            </div>
            <p className="mt-1 text-xs text-tinta-3">
              {ESTRATEGIAS.find((e) => e.key === estrategiaActual)?.desc ??
                "Se usará la estrategia configurada en el panel."}
            </p>
          </div>

          {preguntas.length > 1 && (
            <label className="flex items-center gap-2 mt-4 cursor-pointer">
              <input type="checkbox" className="accent-marca" checked={transversal}
                     onChange={(e) => setTransversal(e.target.checked)} />
              <span className="text-sm text-tinta-2">
                Añadir una lectura transversal de las {preguntas.length} preguntas juntas
                <span className="text-tinta-3"> (una llamada más)</span>
              </span>
            </label>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-4">
            <button className="btn-sec" disabled={!listo || estimar.isPending} onClick={() => estimar.mutate()}>
              {estimar.isPending ? "Estimando…" : "Estimar"}
            </button>
            <button className="btn" disabled={!listo || analizar.isPending} onClick={() => analizar.mutate()}>
              {analizar.isPending ? "Analizando…" : "Analizar"}
            </button>
            {(estimar.isPending || analizar.isPending) && <Spinner />}
            {!listo && <span className="text-xs text-tinta-3">Elige encuesta y al menos una pregunta.</span>}
          </div>

          {analizar.isPending && (
            <p className="mt-2 text-xs text-tinta-3">
              Esto puede tardar varios minutos: son una o más llamadas al modelo, y
              con muchas respuestas se trocean en bloques. No cierres la pestaña.
            </p>
          )}

          {estimar.isError && <div className="mt-3"><ErrorBanner message={(estimar.error as Error).message} /></div>}
          {analizar.isError && (
            <div className="mt-3 space-y-2">
              <ErrorBanner message={(analizar.error as Error).message} />
              {/* Un corte de la conexión (504, «Failed to fetch») no significa que el
                  análisis no se hiciera: el servidor sigue trabajando y lo guarda.
                  Decirlo evita que se vuelva a lanzar —y a pagar— sin necesidad. */}
              {/504|502|timeout|Failed to fetch|NetworkError/i.test(
                (analizar.error as Error).message
              ) && (
                <InfoBanner
                  message={
                    "Se cortó la conexión mientras el servidor seguía trabajando. El análisis " +
                    "puede haber terminado igualmente: mira «Análisis guardados» más abajo antes " +
                    "de volver a lanzarlo."
                  }
                />
              )}
            </div>
          )}

          {estimacion && (
            <div className="mt-4 border border-grid rounded-md p-3 bg-plano">
              <div className="text-sm font-medium text-tinta-2 mb-2">
                Antes de gastar nada
              </div>
              <table className="w-full text-sm">
                <thead className="text-tinta-3 border-b border-grid">
                  <tr>
                    <th className="text-left py-1 pr-3">Pregunta</th>
                    <th className="text-right py-1 pr-3">A analizar</th>
                    <th className="text-right py-1 pr-3">Segmentos</th>
                    <th className="text-right py-1 pr-3">Llamadas</th>
                    <th className="text-right py-1 pr-3">Tokens aprox.</th>
                    <th className="text-right py-1 pr-3">Coste aprox.</th>
                  </tr>
                </thead>
                <tbody>
                  {estimacion.preguntas.map((p) => (
                    <tr key={p.pregunta} className="border-b border-grid">
                      <td className="py-1 pr-3 text-tinta">{p.etiqueta}</td>
                      <td className="py-1 pr-3 text-right">{p.n_a_analizar} / {p.n_disponibles}</td>
                      <td className="py-1 pr-3 text-right">{p.n_segmentos}</td>
                      <td className="py-1 pr-3 text-right">{p.llamadas}</td>
                      <td className="py-1 pr-3 text-right">{p.tokens_entrada_aprox.toLocaleString()}</td>
                      <td className="py-1 pr-3 text-right">
                        {p.costo_estimado ? `$${p.costo_estimado.toFixed(4)}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-xs text-tinta-3">
                {estimacion.llamadas_totales} llamada(s) en total
                {estimacion.costo_estimado_total > 0
                  ? ` · alrededor de $${estimacion.costo_estimado_total.toFixed(4)}`
                  : " · el coste sale en blanco porque no has puesto los precios en Configuración"}.
                {" "}Es una estimación por caracteres, no una cifra exacta.
              </p>
              {!estimacion.configurada && (
                <div className="mt-2">
                  <ErrorBanner message="No hay clave de API configurada: el análisis fallará. Ponla en Configuración → Inteligencia artificial." />
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {analizar.data && (
        <Card className="mb-4">
          <h2 className="text-lg font-semibold text-tinta mb-3">Resultado</h2>
          {analizar.data.fallos.map((f, i) => (
            <div key={i} className="mb-3">
              <ErrorBanner message={`${f.etiqueta || f.pregunta}: ${f.error}`} />
            </div>
          ))}
          {analizar.data.analisis.map((a) => (
            <div key={a.id} className="mb-6">
              <div className="text-sm font-medium text-marca mb-2">
                {a.question_label || a.question_name}
              </div>
              {a.resultado && <Resultado r={a.resultado} />}
            </div>
          ))}
          {analizar.data.lectura_transversal && (
            <div className="border-t border-grid pt-4">
              <div className="text-sm font-medium text-tinta-2 mb-2">Lectura transversal</div>
              <pre className="text-xs bg-plano border border-grid rounded p-3 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(analizar.data.lectura_transversal, null, 2)}
              </pre>
            </div>
          )}
        </Card>
      )}

      {formId && (historicoQ.data ?? []).length > 0 && (
        <Card>
          <h2 className="text-lg font-semibold text-tinta mb-1">Análisis guardados</h2>
          <p className="text-sm text-tinta-3 mb-3">
            De esta encuesta, del más reciente al más antiguo.
          </p>
          {borrar.isError && (
            <div className="mb-3"><ErrorBanner message={(borrar.error as Error).message} /></div>
          )}
          {historicoQ.data!.map((a) => (
            <Guardado key={a.id} a={a} onBorrar={(id) => borrar.mutate(id)} />
          ))}
        </Card>
      )}
    </div>
  );
}
